import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import notification from "../../apiConfigs/notifications";
import { ReactComponent as Check } from "../../assets/check.svg";
import { ReactComponent as Clock } from "../../assets/clock.svg";
import { ReactComponent as Dot } from "../../assets/dot.svg";
import Pagination from "../../components/Pagination";
import Button from "../../designComponents/Button";
import { ButtonTab } from "../../designComponents/ButtonTab";
import Typography from "../../designComponents/Typography";
import { useSocket } from "../../Provider/SocketProvider";
import { humanReadableDateWithTime } from "../../utils/date";
import requestAPI from "../../utils/requestAPI";
import { resizeImage } from "../../utils/resizeImages";
import {
  LabelText,
  Title,
  ValueText,
  Wrapper,
} from "../Properties/Details/styles";
// Define the types for notifications and the state

type CardKey = "all" | "true" | "false" | null;

const Notifications: React.FC = () => {
  const location = useLocation();
  const param = new URLSearchParams(location.search);
  const [cardKey, setCardKey] = useState<CardKey>("all");
  const [pagination, setPagination] = useState<{
    totalData?: number;
    totalPages?: number;
    currentPage: number;
    limit: number;
  }>({ currentPage: 1, limit: 10 });
  const userId = param.get("userId");
  const propertyId = param.get("propertyId");
  const [notificationData, setNotificationData] = useState<any>([]);
  const getAllNotifications = async () => {
    try {
      if (propertyId) {
        const response = await requestAPI(
          notification.getNotifications({
            isRead:
              cardKey === "all" ? null : cardKey === "false" ? false : true,
            page: pagination.currentPage,
            limit: pagination.limit,
            resourceId: propertyId,
          })
        );

        setNotificationData(response.data);

        setPagination(response?.pagination);
      } else if (userId) {
        const response = await requestAPI(
          notification.getNotifications({
            isRead:
              cardKey === "all" ? null : cardKey === "false" ? false : true,
            page: pagination.currentPage,
            limit: pagination.limit,
            notifiedTo: userId,
          })
        );

        setNotificationData(response.data);

        setPagination(response?.pagination);
      } else {
        const response = await requestAPI(
          notification.getNotifications({
            isRead:
              cardKey === "all" ? null : cardKey === "false" ? false : true,
            page: pagination.currentPage,
            limit: pagination.limit,
          })
        );

        setNotificationData(response.data);

        setPagination(response?.pagination);
      }
    } catch (error: any) {
      toast.error(`Notifications fetch failed ${error?.message}`);
    }
  };
  const navigate = useNavigate();
  const handleMarkAsRead = async (notificationId: string[]) => {
    try {
      await requestAPI(notification.markasRead({ notificationId }));
      setNotificationData((prev: any) => {
        return prev.map((notification: any) => {
          if (notificationId.includes(notification._id)) {
            return { ...notification, isRead: true };
          }
          return notification;
        });
      });
      getAllNotifications();
      toast.success("Marked as read");
    } catch (error: any) {
      toast.error(`Failed to mark as read ${error.message}`);
    }
  };
  const { unreadNotifications } = useSocket();

  useEffect(() => {
    getAllNotifications();
  }, [cardKey, pagination.currentPage, pagination.limit]);

  const cardData = [
    { title: "All", key: "all" },
    { title: "Unread", key: "false", count: unreadNotifications?.unreadCount },
    { title: "Read", key: "true" },
  ];

  const handleClick = (key: CardKey) => {
    setCardKey(key);
  };
  const handleView = ({
    resourceId,
    type,
  }: {
    resourceId: string;
    type: string;
  }) => {
    if (type === "BOOKING") {
      navigate(`/bookings/${resourceId}`);
    }
    if (type === "PROPERTY") {
      navigate(`/properties/${resourceId}`);
    }
    if (type === "CONTRACT") {
      navigate(`/contract/${resourceId}`);
    }
  };
  return (
    <Wrapper>
      <div>
        <Title>Notifications</Title>
        <div style={{ width: 200, paddingTop: 20 }}>
          <ButtonTab
            ghost
            items={cardData}
            activeIndex={cardData.findIndex((x) => x.key === cardKey)}
            setActiveIndex={(index) =>
              handleClick(cardData[index].key as CardKey)
            }
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: 45,
            marginTop: 20,
            marginBottom: 20,
            flexDirection: "column",
          }}
        >
          {notificationData.length > 0 ? (
            notificationData.map((notification: any, index: number) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 15,
                  backgroundColor: "#fcfcfc",
                  padding: 12,
                  borderRadius: 8,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Typography style={{ fontSize: 15, color: "#0C133D" }}>
                    {notification?.title}
                  </Typography>
                  <Dot
                    height={8}
                    width={8}
                    fill={
                      notification.isRead === true ? "transparent" : "#0A56AF"
                    }
                  ></Dot>
                </div>

                <ul
                  style={{ display: "flex", flexDirection: "column", gap: 20 }}
                >
                  <div style={{ display: "flex", gap: 18 }}>
                    <img
                      alt={`${index}`}
                      src={
                        resizeImage(notification?.userId?.avatar, "sm") ||
                        "https://e7.pngegg.com/pngimages/84/165/png-clipart-united-states-avatar-organization-information-user-avatar-service-computer-wallpaper-thumbnail.png"
                      }
                      width={60}
                      height={60}
                      style={{ borderRadius: 50 }}
                    ></img>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <ValueText>
                        {notification?.userId?.firstName}
                        {notification?.userId?.lastName}
                      </ValueText>
                      <Typography size="h5">
                        {notification?.description}
                      </Typography>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <Clock fill="#7E7E7E"></Clock>
                        <Typography size="h5" style={{ color: "#7E7E7E" }}>
                          {humanReadableDateWithTime(notification?.createdAt)}
                        </Typography>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          gap: 14,
                          alignItems: "center",
                        }}
                      >
                        <Button
                          radius="normal"
                          style={{
                            color: "#0A56AF",
                            background: "transparent",
                            border: "1px solid #0A56AF",
                          }}
                          onClick={() => {
                            handleView({
                              resourceId: notification?.resourceId,
                              type: notification?.notificationType,
                            });
                          }}
                        >
                          View
                        </Button>
                        <div style={{ display: "flex", gap: 0 }}>
                          <Check
                            fill={notification.isRead ? "#0A56AF" : "#CCD8EE"}
                          ></Check>
                          <Check
                            fill={notification.isRead ? "#0A56AF" : "#CCD8EE"}
                            style={{ position: "relative", left: -6 }}
                          ></Check>
                        </div>

                        {!notification.isRead && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 14,
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleMarkAsRead([notification?._id]);
                            }}
                          >
                            <Typography style={{ color: "#0A56AF" }}>
                              Mark as read
                            </Typography>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </ul>
              </div>
            ))
          ) : (
            <LabelText style={{ paddingTop: 20 }}>No notifications</LabelText>
          )}
        </div>
        <Pagination
          activePage={pagination?.currentPage}
          totalPages={pagination?.totalPages}
          limit={pagination?.limit}
          onCurrentPageChange={(page) =>
            setPagination((prev) => ({ ...prev, currentPage: page }))
          }
          onLimitChange={(newLimit) =>
            setPagination((prev) => ({ ...prev, limit: newLimit }))
          }
        />
      </div>
    </Wrapper>
  );
};

export default Notifications;
